import type { CustomerMessage, MessageMarker } from "@/types/chat"
import type { PlaceRef } from "@/types/entity"
import type { PlacePlaceholder, PlaceSummary } from "@/types/place"

type GraphqlOperationType = "query" | "mutation"

interface GraphqlOperation {
  type: GraphqlOperationType | null
  roots: string[] | null
}

function isGraphqlNameStart(character: string) {
  const code = character.charCodeAt(0)
  return (
    character === "_" ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122)
  )
}

function isGraphqlNameCharacter(character: string) {
  const code = character.charCodeAt(0)
  return isGraphqlNameStart(character) || (code >= 48 && code <= 57)
}

function graphqlOperation(document: string): GraphqlOperation {
  let index = 0
  let type: GraphqlOperationType | null = null

  while (index < document.length) {
    if (document.charAt(index).trim() === "") {
      index += 1
      continue
    }
    if (document.charAt(index) !== "#") {
      break
    }
    while (
      index < document.length &&
      !"\n\r".includes(document.charAt(index))
    ) {
      index += 1
    }
  }
  if (document.charAt(index) === "{") {
    type = "query"
  } else {
    const start = index
    while (isGraphqlNameCharacter(document.charAt(index))) {
      index += 1
    }
    const operation = document.slice(start, index)
    type = operation === "query" || operation === "mutation" ? operation : null
  }

  if (!type) {
    return { type, roots: null }
  }

  let selectionStart =
    type === "query" && document.charAt(index) === "{" ? index : -1
  let parentheses = 0
  let brackets = 0
  while (selectionStart === -1 && index < document.length) {
    const character = document.charAt(index)
    if (character === '"') {
      const block = document.slice(index, index + 3) === '"""'
      index += block ? 3 : 1
      while (index < document.length) {
        if (block && document.slice(index, index + 3) === '"""') {
          index += 3
          break
        }
        if (!block && document.charAt(index) === '"') {
          index += 1
          break
        }
        index += document.charAt(index) === "\\" && !block ? 2 : 1
      }
      continue
    }
    if (character === "#") {
      while (
        index < document.length &&
        !"\n\r".includes(document.charAt(index))
      ) {
        index += 1
      }
      continue
    }
    if (character === "(") parentheses += 1
    if (character === ")") parentheses -= 1
    if (character === "[") brackets += 1
    if (character === "]") brackets -= 1
    if (parentheses < 0 || brackets < 0) return { type, roots: null }
    if (character === "{" && parentheses === 0 && brackets === 0) {
      selectionStart = index
      break
    }
    index += 1
  }
  if (selectionStart === -1 || parentheses !== 0 || brackets !== 0) {
    return { type, roots: null }
  }

  const roots: string[] = []
  let braces = 1
  parentheses = 0
  brackets = 0
  index = selectionStart + 1
  while (index < document.length && braces > 0) {
    const character = document.charAt(index)
    if (character === '"') {
      const block = document.slice(index, index + 3) === '"""'
      index += block ? 3 : 1
      while (index < document.length) {
        if (block && document.slice(index, index + 3) === '"""') {
          index += 3
          break
        }
        if (!block && document.charAt(index) === '"') {
          index += 1
          break
        }
        index += document.charAt(index) === "\\" && !block ? 2 : 1
      }
      continue
    }
    if (character === "#") {
      while (
        index < document.length &&
        !"\n\r".includes(document.charAt(index))
      ) {
        index += 1
      }
      continue
    }
    if (character === "(") parentheses += 1
    if (character === ")") parentheses -= 1
    if (character === "[") brackets += 1
    if (character === "]") brackets -= 1
    if (parentheses < 0 || brackets < 0) return { type, roots: null }
    if (character === "{" && parentheses === 0 && brackets === 0) braces += 1
    if (character === "}" && parentheses === 0 && brackets === 0) braces -= 1
    if (
      braces === 1 &&
      parentheses === 0 &&
      brackets === 0 &&
      character === "."
    ) {
      return { type, roots: null }
    }
    if (
      braces === 1 &&
      parentheses === 0 &&
      brackets === 0 &&
      character === "@"
    ) {
      index += 1
      while (isGraphqlNameCharacter(document.charAt(index))) index += 1
      continue
    }
    if (
      braces === 1 &&
      parentheses === 0 &&
      brackets === 0 &&
      isGraphqlNameStart(character)
    ) {
      const start = index
      index += 1
      while (isGraphqlNameCharacter(document.charAt(index))) index += 1
      let root = document.slice(start, index)
      while (index < document.length && document.charAt(index).trim() === "") {
        index += 1
      }
      if (document.charAt(index) === ":") {
        index += 1
        while (
          index < document.length &&
          document.charAt(index).trim() === ""
        ) {
          index += 1
        }
        if (!isGraphqlNameStart(document.charAt(index)))
          return { type, roots: null }
        const targetStart = index
        index += 1
        while (isGraphqlNameCharacter(document.charAt(index))) index += 1
        root = document.slice(targetStart, index)
      }
      roots.push(root)
      continue
    }
    index += 1
  }

  if (
    braces !== 0 ||
    parentheses !== 0 ||
    brackets !== 0 ||
    roots.length === 0
  ) {
    return { type, roots: null }
  }
  while (index < document.length) {
    if (document.charAt(index).trim() === "") {
      index += 1
      continue
    }
    if (document.charAt(index) !== "#") {
      break
    }
    while (
      index < document.length &&
      !"\n\r".includes(document.charAt(index))
    ) {
      index += 1
    }
  }
  return index === document.length ? { type, roots } : { type, roots: null }
}

// The progress line a tool call runs under. `labels` is the backend's
// localized voice_tool_progress bundle from the session create response; the
// RTC resolves this when a call starts, known skill tools may derive copy from
// structured machine arguments, and the gallery passes its own complete map.
// Unmapped tools use generic copy rather than exposing their machine names.
export function toolCallProgressLabel(
  toolName: string,
  labels: Record<string, string> = {},
  toolArguments?: Record<string, unknown>,
  graphqlProgressLabels: Record<string, string> = {}
) {
  const label = labels[toolName]
  if (label) {
    return label
  }
  if (toolName === "itinerary_undo_redo") {
    return "Updating your itinerary…"
  }
  if (toolName === "graphql_query") {
    const query = toolArguments?.query
    const operation =
      typeof query === "string"
        ? graphqlOperation(query)
        : { type: null, roots: null }
    const generic =
      operation.type === "mutation"
        ? "Updating your plans…"
        : "Checking your plans…"
    if (!operation.roots || operation.roots.length !== 1) {
      return generic
    }
    return graphqlProgressLabels[operation.roots.join()] ?? generic
  }
  return "Working…"
}

// Builds a render-compatible message shell for the shared marker parser. Its
// synthetic defaults are not persisted-message authority; annotations retain
// their real message id when persistence has supplied one.
export function transcriptMessageForBubble({
  text,
  messageId,
  markers,
  places,
}: {
  text: string
  messageId: number | undefined
  markers: MessageMarker[]
  places: PlaceSummary[]
}): CustomerMessage {
  return {
    type: "customer",
    id: messageId ?? 0,
    body: text,
    timestamp: { epoch_millis: 0 },
    markers,
    places,
    inspirations: [],
    lock_version: 0,
  }
}

// Supplies only the display shape required by the place button. Entity identity
// and name are authoritative; zero coordinates/category scaffolding must never
// be treated as resolved place data.
export function voicePlacePlaceholder(
  entityRef: PlaceRef,
  name: string
): PlacePlaceholder {
  const place = {
    type: "location",
    entity_ref: entityRef,
    name,
    latitude: 0,
    longitude: 0,
    category: "city",
    categories: [],
  } satisfies PlacePlaceholder

  return place
}
